/**
 * flattenProposalBreakdown — pure function
 * Transforms BreakdownInvestmentResponse (nested: service → standard → serviceItem)
 * into FlattenedProposalRow[] (one row per service-item, grand-total unitPrice).
 *
 * BreakdownInvestmentResponse shape (from backend):
 * {
 *   years: List<Integer>,
 *   breakdown: List<ServiceBreakdown>,   // { service, standards[] }
 *   totals: List<BigDecimal>
 * }
 * ServiceBreakdown: { service: string, standards: List<StandardBreakdown> }
 * StandardBreakdown: { standard: string, version: string, serviceItems: List<ServiceItemBreakdown> }
 * ServiceItemBreakdown: { serviceItem: string, qty: Double, unit: string, prices: List<BigDecimal>, years: List<Integer> }
 */
export const flattenProposalBreakdown = (response) => {
  if (!response?.breakdown || !Array.isArray(response.breakdown) || response.breakdown.length === 0) {
    return [];
  }

  const rows = [];
  let globalIdx = 0;

  for (const serviceBlock of response.breakdown) {
    const serviceName = serviceBlock.service || '';
    const standards = serviceBlock.standards || [];

    for (const standardBlock of standards) {
      const standardName = standardBlock.standard || '';
      const standardVersion = standardBlock.version || '';
      const serviceItems = standardBlock.serviceItems || [];

      for (const item of serviceItems) {
        const prices = item.prices || [];
        const yearlyPrices = prices.map((p) =>
          typeof p === 'number' ? p : parseFloat(String(p).replace(/[^\d.-]/g, '')) || 0
        );

        // Keep the original/native unit price. `prices` contains converted
        // yearly totals and must not replace the quoted source-currency price.
        const unitPrice = item.unitPrice != null ? Number(item.unitPrice) : 0;

        const lineTotal = (item.qty || 0) * unitPrice;

        // Build description: "ISO 9001:2015 / Sertifikasi / Registration Fee"
        const versionedStandard = standardVersion
          ? `${standardName}:${standardVersion}`
          : standardName;
        const description = [versionedStandard, serviceName, item.serviceItem]
          .filter(Boolean)
          .join(' / ');

        rows.push({
          id: String(globalIdx++),
          serviceName,
          standardName,
          standardVersion,
          serviceItem: item.serviceItem || '',
          description,
          qty: item.qty || 1,
          unit: item.unit || '',
          unitPrice,
          yearlyPrices,
          years: item.years || [],
          currencyCode: item.currencyCode || 'IDR',
          exchangeRateAtCreation: Number(item.exchangeRate || item.exchangeRateAtCreation || 1),
          checked: true,
          lineTotal,
        });
      }
    }
  }

  return rows;
};

export default flattenProposalBreakdown;
